import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../constants';
import type { Tag } from '../types';

// Convert Firestore document to Tag
function docToTag(docSnap: any): Tag {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.() || new Date(),
  };
}

interface UseTagsReturn {
  tags: Tag[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useTags(): UseTagsReturn {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTags = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const q = query(collection(db, COLLECTIONS.tags), orderBy('count', 'desc'));
      const snapshot = await getDocs(q);

      const fetchedTags = snapshot.docs.map(docToTag);
      setTags(fetchedTags);
    } catch (err: any) {
      console.error('Error fetching tags:', err);
      setError('태그를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  return { tags, loading, error, refresh: fetchTags };
}

// Tag mutations
export interface CreateTagData {
  name: string;
  slug: string;
}

export interface UpdateTagData {
  name?: string;
  slug?: string;
}

export function useTagMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTag = async (data: CreateTagData): Promise<string> => {
    setLoading(true);
    setError(null);

    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.tags), {
        ...data,
        count: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (err: any) {
      console.error('Error creating tag:', err);
      setError('태그 생성에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTag = async (tagId: string, data: UpdateTagData): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, COLLECTIONS.tags, tagId);
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error updating tag:', err);
      setError('태그 수정에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async (tagId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await deleteDoc(doc(db, COLLECTIONS.tags, tagId));
    } catch (err: any) {
      console.error('Error deleting tag:', err);
      setError('태그 삭제에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Increment/decrement tag count
  const incrementTagCount = async (tagId: string, delta: number = 1): Promise<void> => {
    try {
      const docRef = doc(db, COLLECTIONS.tags, tagId);
      await updateDoc(docRef, {
        count: increment(delta),
        updatedAt: serverTimestamp(),
      });
    } catch (err: any) {
      console.error('Error updating tag count:', err);
    }
  };

  return {
    createTag,
    updateTag,
    deleteTag,
    incrementTagCount,
    loading,
    error,
  };
}
